import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { queryClient, withClientId } from '@/lib/queryClient'
import {
  fetchBoats,
  fetchBoatById,
  fetchBoatImages,
  fetchLoanByBoat,
  fetchSlipForBoat,
  fetchWorkOrdersForBoat,
  createBoat,
  updateBoat,
  deleteBoat,
  uploadBoatImage,
  updateBoatImage,
  deleteBoatImage,
  createBoatLoan,
  updateBoatLoan,
  deleteBoatLoan,
  payoffBoatLoan,
  payCurtailment,
  unpayCurtailment,
  payInterestCharge,
  unpayInterestCharge,
} from './api'
import type { Boat, BoatLoanWriteBody, BoatWriteBody, ListingState } from '@/types/inventory'
import type { SoldDateRange } from './api'
export const BOATS_KEY = ['boats'] as const

export const boatImagesKey = (boatId: string) => ['boats', boatId, 'images'] as const
export const boatLoanKey = (boatId: string) => ['boats', boatId, 'loan'] as const

// Floor-plan query keys also live under the ["boats", ...] umbrella so the
// broad invalidation refreshes them together with the boat's other data.
export const FLOORPLAN_SUMMARY_KEY = ['floorplan-summary'] as const

export const useBoats = (
  listingState?: ListingState,
  q?: string,
  excludeListingState?: ListingState,
  soldRange?: SoldDateRange,
) => {
  const key: [string, Record<string, unknown>] = ['boats', {}]
  if (listingState) key[1].listing_state = listingState
  if (q) key[1].q = q
  if (excludeListingState) key[1].exclude_listing_state = excludeListingState
  if (soldRange?.from) key[1].sold_from = soldRange.from
  if (soldRange?.to) key[1].sold_to = soldRange.to
  return useQuery({
    queryKey: key,
    queryFn: () => fetchBoats(listingState, q, excludeListingState, soldRange),
  })
}

export const useBoat = (boatId: string) => {
  return useQuery({
    queryKey: ['boats', boatId],
    queryFn: () => fetchBoatById(boatId),
    // Only fetch if boatId is actually present
    enabled: !!boatId,
  })
}

// Cross-module: slip currently assigned to this boat (null = none).
export const useSlipForBoat = (boatId: string | undefined) => {
  return useQuery({
    queryKey: ['slips', 'by-boat', boatId],
    queryFn: () => fetchSlipForBoat(boatId!),
    enabled: !!boatId,
  })
}

// Active floor-plan loan on this boat (null = unencumbered).
export const useLoanByBoat = (boatId: string | undefined) => {
  return useQuery({
    queryKey: boatLoanKey(boatId!),
    queryFn: () => fetchLoanByBoat(boatId!),
    enabled: !!boatId,
  })
}

// Cross-module: work orders for this boat.
export const useWorkOrdersForBoat = (boatId: string | undefined) => {
  return useQuery({
    queryKey: ['work-orders', 'by-boat', boatId],
    queryFn: () => fetchWorkOrdersForBoat(boatId!),
    enabled: !!boatId,
  })
}

// Outbox keys: replay goes through the setMutationDefaults registration below
// (see apps/frontend/AGENTS.md "offline outbox" for the contract). Image
// UPLOAD is deliberately not outboxed — its FormData variables can't be
// serialized into the persisted queue, so it fails fast offline instead.
export const BOAT_CREATE_KEY = ['outbox', 'boats', 'create'] as const
export const BOAT_UPDATE_KEY = ['outbox', 'boats', 'update'] as const
export const BOAT_DELETE_KEY = ['outbox', 'boats', 'delete'] as const
export const BOAT_IMAGE_UPDATE_KEY = ['outbox', 'boat-images', 'update'] as const
export const BOAT_IMAGE_DELETE_KEY = ['outbox', 'boat-images', 'delete'] as const

queryClient.setMutationDefaults(BOAT_CREATE_KEY, { mutationFn: createBoat })
queryClient.setMutationDefaults(BOAT_UPDATE_KEY, {
  mutationFn: (vars: { id: string; body: BoatWriteBody }) => updateBoat(vars.id, vars.body),
})
queryClient.setMutationDefaults(BOAT_DELETE_KEY, { mutationFn: deleteBoat })
queryClient.setMutationDefaults(BOAT_IMAGE_UPDATE_KEY, {
  mutationFn: (vars: {
    boatId: string
    imageId: string
    body: { is_cover?: boolean; sort_order?: number; title?: string; category?: string }
  }) => updateBoatImage(vars.boatId, vars.imageId, vars.body),
})
queryClient.setMutationDefaults(BOAT_IMAGE_DELETE_KEY, {
  mutationFn: (vars: { boatId: string; imageId: string }) =>
    deleteBoatImage(vars.boatId, vars.imageId),
})

// The client-generated id rides in the mutation variables (not inside the
// mutationFn) so an offline replay re-sends the exact same id.
export const useCreateBoat = () => {
  const qc = useQueryClient()
  const mutation = useMutation<Boat, Error, BoatWriteBody & { id: string }>({
    mutationKey: BOAT_CREATE_KEY,
    mutationFn: createBoat,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['boats'] })
      // Adopted intake inspections changed boats server-side (inline literal:
      // importing the inspection hooks here would be a circular import).
      qc.invalidateQueries({ queryKey: ['inspections'] })
    },
  })
  return {
    ...mutation,
    mutate: (body: BoatWriteBody, options?: Parameters<typeof mutation.mutate>[1]) =>
      mutation.mutate(withClientId(body), options),
    mutateAsync: (body: BoatWriteBody, options?: Parameters<typeof mutation.mutateAsync>[1]) =>
      mutation.mutateAsync(withClientId(body), options),
  }
}

export const useUpdateBoat = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: BOAT_UPDATE_KEY,
    mutationFn: (vars: { id: string; body: BoatWriteBody }) => updateBoat(vars.id, vars.body),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['boats'] })
      qc.invalidateQueries({ queryKey: ['boats', data.id] })
    },
  })
}

export const useDeleteBoat = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: BOAT_DELETE_KEY,
    mutationFn: (id: string) => deleteBoat(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['boats'] }),
  })
}

// --- Floor-plan loans ---

// Loan mutations invalidate the boat queries (the embedded loan brief and the
// schedule query) plus the finance floor-plan summary, whose exposure totals
// move with every loan change.

const invalidateBoatLoans = (qc: ReturnType<typeof useQueryClient>, boatId: string) => {
  qc.invalidateQueries({ queryKey: ['boats', boatId] })
  qc.invalidateQueries({ queryKey: ['boats'] })
  qc.invalidateQueries({ queryKey: FLOORPLAN_SUMMARY_KEY })
}

// Loan payments (curtailment or interest) also move the finance ledger (an
// expense entry is booked per payment), so the finance queries refresh too.
const invalidateLoanPayment = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: FLOORPLAN_SUMMARY_KEY })
  qc.invalidateQueries({ queryKey: ['transactions'] })
  qc.invalidateQueries({ queryKey: ['pnl'] })
  qc.invalidateQueries({ queryKey: ['finance-trend'] })
}

export const BOAT_LOAN_CREATE_KEY = ['outbox', 'boat-loans', 'create'] as const
export const BOAT_LOAN_UPDATE_KEY = ['outbox', 'boat-loans', 'update'] as const
export const BOAT_LOAN_DELETE_KEY = ['outbox', 'boat-loans', 'delete'] as const
export const BOAT_LOAN_PAYOFF_KEY = ['outbox', 'boat-loans', 'payoff'] as const
export const CURTAILMENT_PAY_KEY = ['outbox', 'boat-loans', 'curtailment-pay'] as const
export const CURTAILMENT_UNPAY_KEY = ['outbox', 'boat-loans', 'curtailment-unpay'] as const
export const INTEREST_PAY_KEY = ['outbox', 'boat-loans', 'interest-pay'] as const
export const INTEREST_UNPAY_KEY = ['outbox', 'boat-loans', 'interest-unpay'] as const

queryClient.setMutationDefaults(BOAT_LOAN_CREATE_KEY, {
  mutationFn: (vars: { boatId: string; body: BoatLoanWriteBody }) =>
    createBoatLoan(vars.boatId, vars.body),
})
queryClient.setMutationDefaults(BOAT_LOAN_UPDATE_KEY, {
  mutationFn: (vars: { loanId: string; body: BoatLoanWriteBody }) =>
    updateBoatLoan(vars.loanId, vars.body),
})
queryClient.setMutationDefaults(BOAT_LOAN_DELETE_KEY, { mutationFn: deleteBoatLoan })
queryClient.setMutationDefaults(BOAT_LOAN_PAYOFF_KEY, {
  mutationFn: (vars: { loanId: string; body: { paid_off_on?: string; payoff_amount?: number } }) =>
    payoffBoatLoan(vars.loanId, vars.body),
})
queryClient.setMutationDefaults(CURTAILMENT_PAY_KEY, {
  mutationFn: (vars: { loanId: string; curtailmentId: string; paidOn?: string }) =>
    payCurtailment(vars.loanId, vars.curtailmentId, vars.paidOn),
})
queryClient.setMutationDefaults(CURTAILMENT_UNPAY_KEY, {
  mutationFn: (vars: { loanId: string; curtailmentId: string }) =>
    unpayCurtailment(vars.loanId, vars.curtailmentId),
})
queryClient.setMutationDefaults(INTEREST_PAY_KEY, {
  mutationFn: (vars: { loanId: string; chargeId: string; paidOn?: string }) =>
    payInterestCharge(vars.loanId, vars.chargeId, vars.paidOn),
})
queryClient.setMutationDefaults(INTEREST_UNPAY_KEY, {
  mutationFn: (vars: { loanId: string; chargeId: string }) =>
    unpayInterestCharge(vars.loanId, vars.chargeId),
})

export const useCreateBoatLoan = (boatId: string) => {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationKey: BOAT_LOAN_CREATE_KEY,
    mutationFn: (vars: { boatId: string; body: BoatLoanWriteBody }) =>
      createBoatLoan(vars.boatId, vars.body),
    onSuccess: (data) => invalidateBoatLoans(qc, data.boat_id),
  })
  return {
    ...mutation,
    mutate: (body: BoatLoanWriteBody, options?: Parameters<typeof mutation.mutate>[1]) =>
      mutation.mutate({ boatId, body: withClientId(body) }, options),
    mutateAsync: (body: BoatLoanWriteBody, options?: Parameters<typeof mutation.mutateAsync>[1]) =>
      mutation.mutateAsync({ boatId, body: withClientId(body) }, options),
  }
}

export const useUpdateBoatLoan = (boatId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: BOAT_LOAN_UPDATE_KEY,
    mutationFn: (vars: { loanId: string; body: BoatLoanWriteBody }) =>
      updateBoatLoan(vars.loanId, vars.body),
    onSuccess: () => invalidateBoatLoans(qc, boatId),
  })
}

export const useDeleteBoatLoan = (boatId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: BOAT_LOAN_DELETE_KEY,
    mutationFn: (loanId: string) => deleteBoatLoan(loanId),
    onSuccess: () => invalidateBoatLoans(qc, boatId),
  })
}

export const usePayoffBoatLoan = (boatId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: BOAT_LOAN_PAYOFF_KEY,
    mutationFn: (vars: {
      loanId: string
      body: { paid_off_on?: string; payoff_amount?: number }
    }) => payoffBoatLoan(vars.loanId, vars.body),
    onSuccess: () => invalidateBoatLoans(qc, boatId),
  })
}

export const usePayCurtailment = (boatId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: CURTAILMENT_PAY_KEY,
    mutationFn: (vars: { loanId: string; curtailmentId: string; paidOn?: string }) =>
      payCurtailment(vars.loanId, vars.curtailmentId, vars.paidOn),
    onSuccess: () => {
      invalidateBoatLoans(qc, boatId)
      invalidateLoanPayment(qc)
    },
  })
}

export const useUnpayCurtailment = (boatId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: CURTAILMENT_UNPAY_KEY,
    mutationFn: (vars: { loanId: string; curtailmentId: string }) =>
      unpayCurtailment(vars.loanId, vars.curtailmentId),
    onSuccess: () => {
      invalidateBoatLoans(qc, boatId)
      invalidateLoanPayment(qc)
    },
  })
}

export const usePayInterestCharge = (boatId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: INTEREST_PAY_KEY,
    mutationFn: (vars: { loanId: string; chargeId: string; paidOn?: string }) =>
      payInterestCharge(vars.loanId, vars.chargeId, vars.paidOn),
    onSuccess: () => {
      invalidateBoatLoans(qc, boatId)
      invalidateLoanPayment(qc)
    },
  })
}

export const useUnpayInterestCharge = (boatId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: INTEREST_UNPAY_KEY,
    mutationFn: (vars: { loanId: string; chargeId: string }) =>
      unpayInterestCharge(vars.loanId, vars.chargeId),
    onSuccess: () => {
      invalidateBoatLoans(qc, boatId)
      invalidateLoanPayment(qc)
    },
  })
}

// --- Boat images ---
// Image queries live under the ["boats", ...] umbrella so the broad ["boats"]
// invalidation (and boat-detail invalidation) refreshes them too.

export const useBoatImages = (boatId: string) => {
  return useQuery({
    queryKey: boatImagesKey(boatId),
    queryFn: () => fetchBoatImages(boatId),
    enabled: !!boatId,
  })
}

export const useUploadBoatImage = (boatId: string) => {
  const qc = useQueryClient()
  return useMutation({
    // FormData can't serialize into the persisted outbox — fail fast offline
    // (the gallery disables the button with an explanation instead).
    networkMode: 'always',
    mutationFn: (vars: { file: File; sortOrder?: number }) =>
      uploadBoatImage(boatId, vars.file, vars.sortOrder),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: boatImagesKey(boatId) })
      qc.invalidateQueries({ queryKey: ['boats', boatId] })
      qc.invalidateQueries({ queryKey: ['boats'] }) // cover_image_url may appear
    },
  })
}

export const useUpdateBoatImage = (boatId: string) => {
  const qc = useQueryClient()
  const mutation = useMutation<
    Awaited<ReturnType<typeof updateBoatImage>>,
    Error,
    {
      boatId: string
      imageId: string
      body: { is_cover?: boolean; sort_order?: number; title?: string; category?: string }
    }
  >({
    mutationKey: BOAT_IMAGE_UPDATE_KEY,
    // boatId rides in the variables (injected below) so an offline replay can
    // rebuild the request path after a reload.
    mutationFn: ({ boatId: bId, imageId, body }) => updateBoatImage(bId, imageId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: boatImagesKey(boatId) })
      qc.invalidateQueries({ queryKey: ['boats', boatId] })
      qc.invalidateQueries({ queryKey: ['boats'] }) // cover may have changed
    },
  })
  return {
    ...mutation,
    mutate: (
      vars: {
        imageId: string
        body: { is_cover?: boolean; sort_order?: number; title?: string; category?: string }
      },
      options?: Parameters<typeof mutation.mutate>[1],
    ) => mutation.mutate({ boatId, ...vars }, options),
    mutateAsync: (
      vars: {
        imageId: string
        body: { is_cover?: boolean; sort_order?: number; title?: string; category?: string }
      },
      options?: Parameters<typeof mutation.mutateAsync>[1],
    ) => mutation.mutateAsync({ boatId, ...vars }, options),
  }
}

export const useDeleteBoatImage = (boatId: string) => {
  const qc = useQueryClient()
  const mutation = useMutation<
    Awaited<ReturnType<typeof deleteBoatImage>>,
    Error,
    { boatId: string; imageId: string }
  >({
    mutationKey: BOAT_IMAGE_DELETE_KEY,
    mutationFn: ({ boatId: bId, imageId }) => deleteBoatImage(bId, imageId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: boatImagesKey(boatId) })
      qc.invalidateQueries({ queryKey: ['boats', boatId] })
      qc.invalidateQueries({ queryKey: ['boats'] }) // cover may have changed
    },
  })
  return {
    ...mutation,
    mutate: (imageId: string, options?: Parameters<typeof mutation.mutate>[1]) =>
      mutation.mutate({ boatId, imageId }, options),
    mutateAsync: (imageId: string, options?: Parameters<typeof mutation.mutateAsync>[1]) =>
      mutation.mutateAsync({ boatId, imageId }, options),
  }
}
